import React, {
  forwardRef,
  PropsWithChildren,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';
import { Modal as MuiModal, Box } from '@mui/material';
import { createPortal } from 'react-dom';

export const FileViewerModalZIndex = 1300;
export const ModalPortalId = 'modal-portal-root';

export type FileViewerModalRef = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

export type FileViewerModalProps = PropsWithChildren<{
  onClose?: () => void;
}>;

const FileViewerModal = forwardRef<FileViewerModalRef, FileViewerModalProps>(
  ({ children, onClose }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

    useEffect(() => {
      if (typeof window !== 'undefined') {
        const target = document.getElementById(ModalPortalId);
        setPortalTarget(target || document.body);
      }
    }, []);

    const open = () => {
      setIsOpen(true);
    };

    const close = () => {
      setIsOpen(false);
      onClose?.();
    };

    useImperativeHandle(ref, () => ({
      isOpen,
      open,
      close,
    }));

    if (!portalTarget) return null;

    return createPortal(
      <MuiModal
        open={isOpen}
        onClose={close}
        sx={{
          zIndex: FileViewerModalZIndex,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            outline: 'none',
            backgroundColor: '#303235',
          }}
        >
          {children}
        </Box>
      </MuiModal>,
      portalTarget,
    );
  },
);

FileViewerModal.displayName = 'FileViewerModal';

export default FileViewerModal;
